import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { AuthProvider, useAuth } from './providers/AuthProvider'
import { QueryProvider } from './providers/QueryProvider'
import { AppShell } from './AppShell'
import { LoginPage } from '../features/auth/LoginPage'
import { PipelinePage } from '../features/pipeline/PipelinePage'
import { DealPanel } from '../features/deals/DealPanel'
import { HelpPage } from '../features/help/HelpPage'
import { ReportsPage } from '../features/reports/ReportsPage'
import { SettingsPage } from '../features/settings/SettingsPage'
import { RouteError } from './RouteError'
import { ErrorState, Skeleton } from '../ui/Feedback'

/** basename must match vite's `base`; both are '/app'. */
const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <AppShell />,
      errorElement: <RouteError />,
      children: [
        {
          element: <PipelinePage />,
          errorElement: <RouteError />,
          children: [
            { index: true, element: null },
            // The panel is a child route so the board stays mounted behind it:
            // closing returns to exactly the view you left, filters and all.
            { path: 'deals/:dealId', element: <DealPanel />, errorElement: <RouteError /> },
          ],
        },
        { path: 'reports', element: <ReportsPage />, errorElement: <RouteError /> },
        { path: 'settings', element: <SettingsPage />, errorElement: <RouteError /> },
        { path: 'help', element: <HelpPage />, errorElement: <RouteError /> },
      ],
    },
  ],
  { basename: '/app' },
)

/**
 * One guard for the whole authenticated app rather than a check per screen —
 * there is nothing to forget (docs/16 section 3.3).
 */
function Gate() {
  const { status } = useAuth()

  if (status === 'loading') {
    return (
      <div style={{ padding: 24, display: 'grid', gap: 12 }}>
        <Skeleton height={28} width={180} />
        <Skeleton height={200} />
      </div>
    )
  }

  if (status === 'unconfigured') {
    return (
      <ErrorState
        title="YourDealPulse is not configured for sign-in"
        body="The server reports that authentication is disabled, and this app has no anonymous mode. Start the backend with its Supabase settings, then reload."
      />
    )
  }

  if (status === 'signed-out') return <LoginPage />

  return <RouterProvider router={router} />
}

export function App() {
  return (
    <AuthProvider>
      <QueryProvider>
        <Gate />
      </QueryProvider>
    </AuthProvider>
  )
}
